import Input from "components/DesignSystem/Input/Input";
import { Button } from "components/DesignSystem/Button/Button";
import Dropdown from "components/DesignSystem/Dropdown/Dropdown";
import { Accordion } from "components/DesignSystem/Accordion/Accordion";
import { Switch } from "components/DesignSystem/Switch/Switch";
import { FormikForm } from "components/FormikForm/FormikForm";
import classNames from "classnames";
import {
  ACTOR_TYPE,
  ActorType,
  INCORPORATION_FORM_SOURCE,
  IncorporationFormSource,
  QUESTION_TYPE,
} from "constants/incorporation";
import { useToast } from "hooks/useToast";
import { useModal } from "hooks/useModal";
import { debounce } from "utils/debouncing";
import ThreeDots from "static/images/ThreeDots.svg";
import { Step } from "store/apis/incorporationFormBuilder";
import { BackendError } from "types/utils/error";
import { MouseEvent, useMemo } from "react";
import {
  getStepProgress,
  isStepAnswered,
} from "util/incorporationFormProgress";
import {
  IncorporationAdminStatusChip,
  IncorporationQuestionRow,
} from "./IncorporationQuestionRow";
import { IncorporationStepStatusChip } from "./IncorporationStepStatusChip";
import { BareRichTextEditor } from "components/DesignSystem/RichTextEditor/BareRichTextEditor";
import { RichTextView } from "components/DesignSystem/RichTextEditor/RichTextView";
import { ConvertStepToReadOnlyModal } from "./ConvertStepToReadOnlyModal";
import { IncorporationLockedFieldTooltip } from "./IncorporationLockedFieldTooltip";
import {
  useCreateFormTreeNodeMutation,
  useDeleteFormTreeNodeMutation,
  useUpdateFormTreeNodeMutation,
} from "./useFormTreeNodeMutations";

const NEW_QUESTION_DEFAULT_LABEL = "Untitled question";
const STEP_NAME_PLACEHOLDER = "Enter step name";
const STEP_DESCRIPTION_PLACEHOLDER =
  "Enter details like who is handling this step and what is it about.";

const IncorporationStepCardActions = ({
  source,
  treeId,
  stageId,
  step,
  isDeleteDisabled = false,
}: {
  source: IncorporationFormSource;
  treeId: string;
  stageId: string;
  step: Step;
  isDeleteDisabled?: boolean;
}) => {
  const { alertToast } = useToast();
  const [duplicateStep] = useCreateFormTreeNodeMutation({ source });
  const [deleteStep] = useDeleteFormTreeNodeMutation({ source });

  const onActionError = (err: unknown) => {
    alertToast({
      message:
        (err as BackendError)?.data?.error?.message ?? "Something went wrong",
    });
  };

  const handleClick = (e: MouseEvent) => {
    e.stopPropagation();
  };

  const handleDuplicate = async () => {
    try {
      const { uuid, status, is_locked, ...stepData } = step;
      await duplicateStep({
        treeId,
        body: { step: { ...stepData, parent_id: stageId } },
      }).unwrap();
    } catch (err) {
      onActionError(err);
    }
  };

  const handleDelete = async () => {
    try {
      await deleteStep({
        treeId,
        body: { step: { node_id: step.uuid } },
      }).unwrap();
    } catch (err) {
      onActionError(err);
    }
  };

  return (
    <div onClick={handleClick}>
      <Dropdown.Root>
        <Dropdown.Trigger asChild>
          <img src={ThreeDots} alt="Show more options" />
        </Dropdown.Trigger>
        <Dropdown.Portal>
          <Dropdown.Content sideOffset={6} align="end">
            <Dropdown.Item onSelect={handleDuplicate}>
              Duplicate step
            </Dropdown.Item>
            <Dropdown.Item
              type="danger"
              disabled={isDeleteDisabled}
              onSelect={handleDelete}
            >
              <IncorporationLockedFieldTooltip isLocked={isDeleteDisabled}>
                <span>Delete step</span>
              </IncorporationLockedFieldTooltip>
            </Dropdown.Item>
          </Dropdown.Content>
        </Dropdown.Portal>
      </Dropdown.Root>
    </div>
  );
};

export const IncorporationStepCard = ({
  step,
  order,
  source,
  treeId,
  stageId,
  isEditable,
  isAnswerable = false,
  isActive = false,
  isPublished = false,
  stepRef,
  groupId,
}: {
  step: Step;
  order: number;
  source: IncorporationFormSource;
  treeId: string;
  stageId: string;
  isEditable: boolean;
  isAnswerable?: boolean;
  isActive?: boolean;
  isPublished?: boolean;
  stepRef?: (node: HTMLDivElement | null) => void;
  groupId?: string;
}) => {
  const { answeredCount, totalCount } = getStepProgress(step);
  const { alertToast } = useToast();
  const convertToReadOnlyModal = useModal();
  const [updateStep, { isLoading: isTogglingReadOnly }] =
    useUpdateFormTreeNodeMutation({ source });
  const [addQuestion, { isLoading: isAddingQuestion }] =
    useCreateFormTreeNodeMutation({ source });

  const onActionError = (err: unknown) => {
    alertToast({
      message:
        (err as BackendError)?.data?.error?.message ?? "Something went wrong",
    });
  };

  const saveTitle = async (title: string) => {
    try {
      await updateStep({
        treeId,
        body: { step: { node_id: step.uuid, title } },
      }).unwrap();
    } catch (err) {
      onActionError(err);
    }
  };

  const saveDescription = async (description: string) => {
    try {
      await updateStep({
        treeId,
        body: { step: { node_id: step.uuid, description } },
      }).unwrap();
    } catch (err) {
      onActionError(err);
    }
  };

  const debouncedSaveTitle = useMemo(() => debounce(saveTitle), [step.uuid]);
  const debouncedSaveDescription = useMemo(
    () => debounce(saveDescription),
    [step.uuid]
  );

  const isReadOnlyStep = step.actor_type === ACTOR_TYPE.ADMIN;
  const isStepLocked = isPublished && isStepAnswered(step);

  const setStepActorType = async (actorType: ActorType) => {
    try {
      await updateStep({
        treeId,
        body: { step: { node_id: step.uuid, actor_type: actorType } },
      }).unwrap();
    } catch (err) {
      onActionError(err);
    }
  };

  const onToggleReadOnlyStep = (checked: boolean) => {
    if (checked) {
      convertToReadOnlyModal.open();
      return;
    }

    setStepActorType(ACTOR_TYPE.CUSTOMER);
  };

  const onConfirmConvertToReadOnly = async () => {
    await setStepActorType(ACTOR_TYPE.ADMIN);
    convertToReadOnlyModal.close();
  };

  const onAddNewItem = async () => {
    try {
      await addQuestion({
        treeId,
        body: {
          question: {
            parent_id: step.uuid,
            label: NEW_QUESTION_DEFAULT_LABEL,
            question_type: isReadOnlyStep
              ? QUESTION_TYPE.READ_ONLY
              : QUESTION_TYPE.TEXT_INPUT,
          },
        },
      }).unwrap();
    } catch (err) {
      onActionError(err);
    }
  };

  if (!isEditable) {
    const isStepComplete = totalCount > 0 && answeredCount === totalCount;
    const isOpenByDefault = isReadOnlyStep || !isStepComplete;

    return (
      <div ref={stepRef} data-step-id={step.uuid}>
        <Accordion.Root
          type="single"
          collapsible
          defaultValue={isOpenByDefault ? step.uuid : undefined}
        >
          <Accordion.RawItem
            value={step.uuid}
            className={classNames("t-rounded-lg t-border t-border-solid", {
              "t-border-neutral-20 t-shadow-box-shadow": isActive,
              "t-border-neutral-10": !isActive,
            })}
          >
            <Accordion.Trigger className="!t-p-5 sm:!t-p-4">
              <div className="t-flex t-min-w-0 t-flex-1 t-items-center t-justify-between t-gap-3 t-normal-case sm:t-flex-col sm:t-items-start sm:t-gap-2">
                <div className="t-flex t-min-w-0 t-flex-1 t-flex-col t-items-start t-gap-0.5">
                  <span className="t-text-subtext-sm t-text-neutral-40">
                    Step {order}
                  </span>
                  <span className="t-text-subtext-lg t-text-text-60">
                    {step.title || "Untitled step"}
                  </span>
                  {step.description && (
                    <RichTextView
                      hasLegacyMarkdown
                      value={step.description}
                      className="t-text-body-sm t-text-text-30"
                    />
                  )}
                </div>
                {source !== INCORPORATION_FORM_SOURCE.TEMPLATE &&
                  isPublished && (
                    <span className="t-shrink-0">
                      {step.actor_type === ACTOR_TYPE.ADMIN ? (
                        <IncorporationAdminStatusChip status={step.status} />
                      ) : (
                        <IncorporationStepStatusChip
                          answeredCount={answeredCount}
                          totalCount={totalCount}
                        />
                      )}
                    </span>
                  )}
              </div>
            </Accordion.Trigger>
            <Accordion.Content className="t-flex t-flex-col t-gap-5 !t-p-5 sm:!t-p-4">
              {step.questions.map((question) => (
                <IncorporationQuestionRow
                  key={question.uuid}
                  question={question}
                  source={source}
                  treeId={treeId}
                  isEditable={false}
                  isAnswerable={isAnswerable}
                  groupId={groupId}
                  isPublished={isPublished}
                />
              ))}
            </Accordion.Content>
          </Accordion.RawItem>
        </Accordion.Root>
      </div>
    );
  }

  return (
    <div ref={stepRef} data-step-id={step.uuid}>
      <Accordion.Root type="single" collapsible defaultValue={step.uuid}>
        <Accordion.RawItem
          value={step.uuid}
          className={classNames("t-rounded-lg t-border t-border-solid", {
            "t-border-neutral-20 t-shadow-box-shadow": isActive,
            "t-border-neutral-10": !isActive,
          })}
        >
          <Accordion.Trigger className="!t-p-5">
            <div className="t-flex t-flex-1 t-items-center t-justify-between t-gap-2 t-normal-case">
              <span className="t-text-subtext-lg t-text-text-60">
                Step {order}
              </span>
              <IncorporationStepCardActions
                source={source}
                treeId={treeId}
                stageId={stageId}
                step={step}
                isDeleteDisabled={isStepLocked}
              />
            </div>
          </Accordion.Trigger>
          <Accordion.Content className="!t-p-0">
            <IncorporationLockedFieldTooltip isLocked={isStepLocked}>
              <div className="t-flex t-flex-col t-gap-3 t-p-5">
                <FormikForm
                  initialValues={{ title: step.title }}
                  onSubmit={() => {}}
                  className="t-flex t-flex-col t-gap-3 !t-border-0"
                >
                  <Input
                    withForm
                    name="title"
                    label="Step name"
                    placeholder={STEP_NAME_PLACEHOLDER}
                    required
                    disabled={isStepLocked}
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                      debouncedSaveTitle(e.target.value)
                    }
                  />
                  <div className="t-flex t-flex-col t-gap-1">
                    <span className="t-text-body-sm t-text-text-60">
                      Step description
                    </span>
                    <BareRichTextEditor
                      hasLegacyMarkdown
                      defaultValue={step.description}
                      placeholder={STEP_DESCRIPTION_PLACEHOLDER}
                      onChange={debouncedSaveDescription}
                      isDisabled={isStepLocked}
                    />
                  </div>
                </FormikForm>

                <div className="t-flex t-items-center t-gap-2 t-pb-2">
                  <Switch
                    size="small"
                    checked={isReadOnlyStep}
                    disabled={isStepLocked}
                    onCheckedChange={onToggleReadOnlyStep}
                  />
                  <span className="t-text-body-sm t-text-text-60">
                    Make this a read only step for customer
                  </span>
                </div>
              </div>
            </IncorporationLockedFieldTooltip>

            {step.questions.map((question) => (
              <div key={question.uuid} className="t-px-5">
                <IncorporationQuestionRow
                  question={question}
                  source={source}
                  treeId={treeId}
                  isEditable
                  isPublished={isPublished}
                />
              </div>
            ))}

            <div className="t-border-0 t-border-t t-border-solid t-border-neutral-10 t-py-3 t-px-5">
              <IncorporationLockedFieldTooltip isLocked={isStepLocked}>
                <Button
                  type="button"
                  customType="primary"
                  isLoading={isAddingQuestion}
                  disabled={isAddingQuestion || isStepLocked}
                  onClick={onAddNewItem}
                  size="small"
                >
                  + Add new item
                </Button>
              </IncorporationLockedFieldTooltip>
            </div>
          </Accordion.Content>
        </Accordion.RawItem>
      </Accordion.Root>
      <ConvertStepToReadOnlyModal
        isOpen={convertToReadOnlyModal.isOpen}
        close={convertToReadOnlyModal.close}
        onConfirm={onConfirmConvertToReadOnly}
        isLoading={isTogglingReadOnly}
      />
    </div>
  );
};
